/**
 * SavingIndicator — inline status badge next to an editable field
 * (Block 6.B.1; microcopy refreshed Block 6.F.4 §3.7).
 *
 * Three states:
 *   - 'saving' → "Saving…" (sage-500; calm present-progressive)
 *   - 'saved'  → "Saved" briefly (sage-700, no checkmark; the absence-
 *                 of-spinner IS the saved cue)
 *   - 'error'  → the caller's sentence for the failure, or "That
 *                 didn't save. Try again, and if it happens again, tell
 *                 {contact_system}." when it has none (warm-orange;
 *                 collaborative recovery, not blame)
 *   - null     → no indicator rendered
 *
 * Voice/tone framing is "warm collaborator, not status reporter or
 * apologetic chatbot." The error sentence comes from src/lib/errorCopy.ts
 * and already says what happened, what is safe and what to do, so it
 * replaces the headline rather than following it.
 */

import type { ReactNode } from 'react';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';

export interface SavingIndicatorProps {
  state: 'saving' | 'saved' | 'error' | null;
  errorMessage?: string;
}

export function SavingIndicator({ state, errorMessage }: SavingIndicatorProps): ReactNode {
  const contact = useSupportContact();
  if (state === null) return null;
  if (state === 'saving') {
    return (
      <span aria-live="polite" data-saving="saving" className="text-xs text-sage-500 ml-2">
        Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span aria-live="polite" data-saving="saved" className="text-xs text-sage-700 ml-2">
        Saved
      </span>
    );
  }
  // warm-700 (warm-orange) rather than red: the failure mode is "let's
  // try again", not "user error".
  return (
    <span aria-live="polite" className="text-xs text-warm-700 ml-2" data-testid="saving-indicator-error">
      {errorMessage ??
        contactSentence("That didn't save. Try again, and if it happens again, tell {contact_system}.", contact)}
    </span>
  );
}
